export interface ProductImageMeta {
  imageId: number;
  fileName?: string | null;
  primary: boolean;
  sortOrder?: number | null;
}

export interface Product {
  productId: number;
  inventoryId?: number | null;
  sortOrder: number | null;
  productCode?: string | null;
  productName: string;
  category?: string | null;
  spec?: string | null;
  barcode?: string | null;
  manufacturer?: string | null;
  unit?: string | null;
  costPrice?: number | null;
  supplyPrice?: number | null;
  stockQty?: number | null;
  safetyStock?: number | null;
  storageLocation?: string | null;
  vatType?: 'TAXABLE' | 'TAX_FREE' | string | null;
  useYn?: 'Y' | 'N' | string | null;
  hasImage?: boolean;
  imageName?: string | null;
  images?: ProductImageMeta[] | null;
  tags?: string[] | null;
  memo?: string | null;
  createdUserId?: number | null;
  createdUserName?: string | null;
  lastUpdatedUserId?: number | null;
  lastUpdatedUserName?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface ProductPayload {
  productCode?: string;
  productName: string;
  category?: string;
  spec?: string;
  barcode?: string;
  manufacturer?: string;
  unit?: string;
  costPrice?: number | null;
  supplyPrice?: number | null;
  stockQty?: number | null;
  safetyStock?: number | null;
  storageLocation?: string;
  vatType?: 'TAXABLE' | 'TAX_FREE';
  useYn?: 'Y' | 'N';
  tags?: string[];
  memo?: string;
  /** 현재고 변경 시 사유 */
  stockAdjustReason?: string;
}

/** 제품 구성 부품(BOM) */
export interface ProductPart {
  partId: number;
  productId: number;
  childProductId?: number | null;
  sortOrder?: number | null;
  partName: string;
  spec?: string | null;
  unit?: string | null;
  qty?: number | null;
  unitPrice?: number | null;
  remark?: string | null;
  amount?: number | null;
}

export interface ProductPartPayload {
  childProductId?: number | null;
  partName?: string;
  spec?: string;
  unit?: string;
  qty?: number | null;
  unitPrice?: number | null;
  remark?: string;
}

/** 회사 공통 인증서 */
export interface CertificateAttachment {
  attachmentId?: number | null;
  fileName: string;
  fileExt?: string | null;
  fileSize?: number | null;
}

export interface Certificate {
  certificateId: number;
  title: string;
  scopes?: string[] | null;
  validFrom?: string | null;
  validTo?: string | null;
  /** 만료 임박 알림 일수 (기본 30) */
  warnDays?: number | null;
  /** OK / EXPIRING / EXPIRED / NONE */
  validityStatus?: string | null;
  content?: string | null;
  fileName?: string | null;
  fileExt?: string | null;
  fileSize?: number | null;
  createdAt?: string | null;
  linkId?: number | null;
  attachments?: CertificateAttachment[] | null;
  reviews?: CertificateReview[] | null;
}

export interface CertificateReview {
  reviewId: number;
  certificateId: number;
  reviewType: string;
  reviewDate?: string | null;
  content?: string | null;
  createdAt?: string | null;
  managers?: CertificateReviewManager[] | null;
}

export interface CertificateReviewManager {
  managerId?: number;
  managerName: string;
  contact?: string | null;
}

export interface CertificateReviewPayload {
  reviewType: string;
  reviewDate: string;
  content?: string;
  managers: CertificateReviewManager[];
}

export interface CertificateUpdatePayload {
  title?: string;
  scopes?: string[];
  validFrom?: string | null;
  validTo?: string | null;
  warnDays?: number | null;
  content?: string;
}

export const CERTIFICATE_REVIEW_TYPES = [
  '공장심사',
  '정기심사',
  '갱신심사',
  '사후관리',
  '기타',
] as const;

/** @deprecated use Certificate */
export type ProductCertificate = Certificate;

export function formatFileSize(bytes?: number | null) {
  const size = bytes ?? 0;
  if (size < 1024) return `${size}B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`;
  return `${(size / (1024 * 1024)).toFixed(1)}MB`;
}

export function formatCertScopes(scopes?: string[] | null) {
  if (!scopes || scopes.length === 0) return '-';
  return scopes.join(', ');
}

export function formatCertPeriod(validFrom?: string | null, validTo?: string | null) {
  const from = validFrom?.slice(0, 10) || '';
  const to = validTo?.slice(0, 10) || '';
  if (!from && !to) return '-';
  if (from && to) return `${from} ~ ${to}`;
  if (from) return `${from} ~`;
  return `~ ${to}`;
}

export function certificateValidityLabel(status?: string | null) {
  switch (status) {
    case 'EXPIRED':
      return '만료';
    case 'EXPIRING':
      return '만료 임박';
    case 'OK':
      return '유효';
    default:
      return '미등록';
  }
}

export function certificateDaysUntil(validTo?: string | null): number | null {
  if (!validTo) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${validTo.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  return Math.round((target.getTime() - today.getTime()) / 86400000);
}

export function certificateRemainText(validTo?: string | null) {
  const days = certificateDaysUntil(validTo);
  if (days == null) return '-';
  if (days < 0) return `${Math.abs(days)}일 지남`;
  if (days === 0) return '오늘 만료';
  return `${days}일 남음`;
}

export const PRODUCT_UNITS = ['EA', 'SET', 'BOX', 'KG', 'M', '식'] as const;

export const PRODUCT_CATEGORIES = ['완제품', '반제품', '원자재', '부품', '소모품', '기타'] as const;

export const PRODUCT_TAG_SUGGESTIONS = [
  'ks인증',
  'ce',
  'ul',
  'fcc',
  'rohs',
  'iso',
  'kc인증',
] as const;

export function normalizeProductTag(raw: string): string {
  return raw.trim().replace(/^#+/, '').trim();
}

export function formatProductTag(tag: string): string {
  const normalized = normalizeProductTag(tag);
  return normalized ? `#${normalized}` : '';
}

export function formatWon(value: number | null | undefined) {
  return `${(value ?? 0).toLocaleString('ko-KR')}원`;
}

export function formatQty(value: number | null | undefined) {
  return (value ?? 0).toLocaleString('ko-KR');
}

export function vatTypeLabel(vatType?: string | null) {
  return vatType === 'TAX_FREE' ? '면세' : '과세';
}

export function formatUseYn(useYn?: string | null) {
  return useYn === 'N' ? '미사용' : '사용';
}
